import React , {Suspense, lazy} from "react";
import LoadingIndicator from "./LoadingIndicator";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

import {useRecoilState} from "recoil";
import {currentUserInfo} from "../atoms/signInAtom";


const HomePage = lazy(() => import('./HomePage'));

const Login = lazy(() => import('./Login'));
const DjangoLogin = lazy(() => import('./DjangoLogin'));
const SignUp = lazy( () => import ("./SignUp"));
const ResetPassword = lazy(() => import("./ResetPassword"));
const Profile = lazy(() => import("./Profile/Profile"));

const CaregiverProfile = lazy(() => import("./Profile/CaregiverProfile"));
const CustomerProfile = lazy(() => import("./Profile/CustomerProfile"));

const EditCustomerProfile = lazy(() => import("./Profile/CustomerProfile/EditCustomerProfile"));

const SignUpProcessCaregiverStep1 = lazy(() => import("./SignUpProcess/Caregiver/Step1"));
const SignUpProcessCaregiverStep2 = lazy(() => import("./SignUpProcess/Caregiver/Step2"));
const SignUpProcessCaregiverStep3 = lazy(() => import("./SignUpProcess/Caregiver/Step3"));
const SignUpProcessCaregiverStep4 = lazy(() => import("./SignUpProcess/Caregiver/Step4"));
const RegistrationSuccessPage = lazy(() => import("./SignUpProcess/RegistrationSuccessPage"));

const SignUpProcessCustomerStep1 = lazy(() => import("./SignUpProcess/Customer/Step1"));
const SignUpProcessCustomerStep2 = lazy(() => import("./SignUpProcess/Customer/Step2"));
const SignUpProcessCustomerStep3 = lazy(() => import("./SignUpProcess/Customer/Step3"));

const ShiftsDashboard = lazy(() => import("./Shifts/Dashboard"));
const ShiftsRequests = lazy(() => import("./Shifts/Requests"));
const ShiftsCreate = lazy(() => import("./Shifts/ShiftsCreate"));
const ShiftsRequestDetails = lazy(() => import("./Shifts/RequestDetails"));
const Availability = lazy(() => import("./Shifts/Availability"));
const CaregiversChoice = lazy(() => import("./Shifts/CaregiversChoice"));

const VisitsDashboard = lazy(() => import("./Visits/Dashboard"));
const Appointments = lazy(() => import("./Visits/Appointments"));
const AppointmentRequest = lazy(() => import("./Visits/AppointmentRequest"));
const AppointmentRequestStep = lazy(() => import("./Visits/AppointmentRequest/requestStep"));
const AppointmentDetails = lazy(() => import("./Visits/AppointmentDetails"));
const Files = lazy(() => import("./Files"));
const SelectCaregiver = lazy(() => import("./Profile/PartnerProfile/SelectCaregiver"));
const CreateAppointmentRequest = lazy(() => import("./Profile/PartnerProfile/CreateAppointmentRequest"));

// TODO: investigate errors when this component loaded with lazy.
import EditCaregiverProfile from "./Profile/CaregiverProfile/EditCaregiverProfile";

// const EditCaregiverProfile = lazy(() => import("./Profile/CaregiverProfile/EditCaregiverProfile"))

const Page404 = lazy(() => import("./404"));
const ProtectedRoute = lazy(() => import("./ProtectedRoute"));

const Routers = () => {
  "use strict";
  const [userInfo] = useRecoilState(currentUserInfo);
  return (
    <Suspense fallback={<LoadingIndicator />}>
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<DjangoLogin />} />
          <Route path="/django-login" element={<DjangoLogin />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/password-reset" element={<ResetPassword />} />

          {/*************** Caregiver **************/}
          <Route path="/sign-up/caregiver/step/1" element={<SignUpProcessCaregiverStep1 role={"caregiver"} />} />
          <Route path="/sign-up/caregiver/step/2" element={<SignUpProcessCaregiverStep2 role={"caregiver"} />} />
          <Route path="/sign-up/caregiver/step/3" element={<SignUpProcessCaregiverStep3 role={"caregiver"} />} />
          <Route path="/sign-up/caregiver/step/4" element={<SignUpProcessCaregiverStep4 role={"caregiver"} />} />

          <Route path="/sign-up/customer/step/1" element={<SignUpProcessCustomerStep1 />} />
          <Route path="/sign-up/customer/step/2" element={<SignUpProcessCustomerStep2 />} />
          <Route path="/sign-up/customer/step/3" element={<SignUpProcessCustomerStep3 />} />

          {/***************Registration success message **************/}
          <Route path="/sign-up/success" element={<RegistrationSuccessPage role={"caregiver"} />} />

          {/***************404**************/}
          <Route path="/page-not-found" element={<Page404 />} />
        </Routes>
        <Routes>
          {/*************** Protected Routes **************/}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/edit"
            element={
              <ProtectedRoute>
                <EditCaregiverProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/edit"
            element={
              <ProtectedRoute>
                <EditCustomerProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/profile"
            element={
              <ProtectedRoute>
                <CaregiverProfile role={"caregiver"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/shifts"
            element={
              <ProtectedRoute>
                <ShiftsDashboard role={"caregiver"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/shifts/requests"
            element={
              <ProtectedRoute>
                <ShiftsRequests role={"caregiver"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/shifts/create"
            element={
              <ProtectedRoute>
                <ShiftsCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/shifts/request/details/:id"
            element={
              <ProtectedRoute>
                <ShiftsRequestDetails role={"caregiver"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/shifts/request/details/:id/:status"
            element={
              <ProtectedRoute>
                <ShiftsRequestDetails role={"caregiver"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/shifts/availability"
            element={
              <ProtectedRoute>
                <Availability role={"caregiver"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver/files"
            element={
              <ProtectedRoute>
                <Files role={"caregiver"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/profile"
            element={
              <ProtectedRoute>
                <CustomerProfile role={"customer"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/visits"
            element={
              <ProtectedRoute>
                <VisitsDashboard role={"customer"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/visits/appointments"
            element={
              <ProtectedRoute>
                <Appointments role={"customer"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/visits/appointment/details/:id"
            element={
              <ProtectedRoute>
                <AppointmentDetails role={"customer"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/visits/request"
            element={
              <ProtectedRoute>
                <AppointmentRequest role={"customer"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/visits/request/step2"
            element={
              <ProtectedRoute>
                <AppointmentRequestStep step={2} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/visits/request/step3"
            element={
              <ProtectedRoute>
                <AppointmentRequestStep step={3} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/visits/request/step4"
            element={
              <ProtectedRoute>
                <AppointmentRequestStep step={4} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/visits/request/payment-error"
            element={
              <ProtectedRoute>
                <AppointmentRequestStep step={0} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/visits/request/caregivers-choice/:id"
            element={
              <ProtectedRoute>
                <CaregiversChoice role={"customer"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/files"
            element={
              <ProtectedRoute>
                <Files role={"customer"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/partner/select-caregiver"
            element={
              <ProtectedRoute>
                <SelectCaregiver role={"partner"} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/partner/create-request"
            element={
              <ProtectedRoute>
                <CreateAppointmentRequest role={"partner"} />
              </ProtectedRoute>
            }
          />
          {/*<Route path="" element={<Page404 />} />*/}
          {/*<Route path="*" element={<Page404 />}/>*/}
        </Routes>
      </Router>
    </Suspense>
  );
};

export default Routers;
